import React, { useState, useEffect, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { Grid, TextField } from '@mui/material';

import { FragmentType, graphql, useFragment } from '../../../__generated__';
import { getSourceCurrencies } from '../../../util/options';
import ErrorView from '../../../components/common/ErrorView';
import CurrencyOption from '../../../components/common/CurrencyOption';
import graphqlClient from '../../../graphql';
import { UPDATE_RATE, ADD_RATE } from '../../../graphql/rate';
import { ClientRate_ItemFragment } from './ClientRateItem';
import { Flag } from '../../../components/types';
import FormDialogContainer, {
  DialogBody,
} from '../../../components/common/FormDialogContainer';

const readTenantQuery = graphql(/* GraphQL */ `
  query ReadTenantForRate($id: ID) {
    tenant(id: $id) {
      currency
    }
  }
`);

type EditClientRateProps = {
  open: boolean;
  tenantId: string;
  rate?: FragmentType<typeof ClientRate_ItemFragment>;
  destinations: Flag[];
  onClose: (itemChanged?: boolean) => void;
};

const EditClientRate = (props: EditClientRateProps) => {
  const { open, tenantId, onClose, destinations } = props;
  const rate = useFragment(ClientRate_ItemFragment, props.rate) || null;
  const [updateRate, { loading: updateLoading, error: updateError }] =
    useMutation(UPDATE_RATE);
  const [addRate, { loading: addLoading, error: addError }] =
    useMutation(ADD_RATE);
  const [source, setSource] = useState<string>(rate?.source || '');
  const [destination, setDestination] = useState<string>(
    rate?.destination || ''
  );
  const [amount, setAmount] = useState<number | string>(
    rate?.rate ? +rate.rate : 0
  );
  const [formError, setFormError] = useState<string>();
  useEffect(() => {
    setSource(rate?.source || '');
    setDestination(rate?.destination || '');
    setAmount(rate?.rate || 0);
    setFormError('');
  }, [rate]);
  const isEdit = useMemo(() => (rate ? Boolean(rate.id) : false), [rate]);

  const loading = rate ? updateLoading : addLoading;
  const error = rate ? updateError : addError;

  const updateCurrencyCallback = (
    newValue: string,
    updateCallback: (value: string) => void
  ): void => {
    updateCallback(newValue);
  };

  function handleAmountChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void {
    setAmount(e.currentTarget.value);
  }

  const allowSubmit = () => {
    const result = source && destination && +amount > 0 && !loading;
    return Boolean(result);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError('');

    if (allowSubmit()) {
      if (rate) {
        const result = await updateRate({
          variables: {
            tenantId,
            id: rate.id,
            rate: +amount,
          },
        });
        if (result?.data?.updateRate?.success) {
          onClose(true);
        }
      } else {
        const result = await addRate({
          variables: {
            tenantId,
            source,
            destination,
            rate: +amount,
          },
        });
        if (result?.data?.addRate?.success) {
          onClose(true);
        }
      }
    } else {
      setFormError(
        'There was an issue with the form submission. Please contact system admin.'
      );
    }
  };

  const localTenant = graphqlClient.readQuery({
    query: readTenantQuery,
    variables: {
      id: tenantId,
    },
  });

  let srcCurrencies = getSourceCurrencies();
  if (localTenant?.tenant) {
    srcCurrencies = srcCurrencies.filter(
      (c) => c.value === localTenant.tenant?.currency
    );
  }

  const errorMessage = formError || error?.message;

  return (
    <>
      <FormDialogContainer>
        <DialogBody
          open={open}
          disabled={!allowSubmit()}
          title={isEdit ? 'Edit Rate' : 'New Rate'}
          onClose={onClose}
          onSubmit={handleSubmit}
        >
          <Grid container spacing={3} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <CurrencyOption
                id={'source'}
                options={srcCurrencies}
                label={'From'}
                value={source}
                onChange={(newValue: string) =>
                  updateCurrencyCallback(newValue, () => setSource(newValue))
                }
                disabled={isEdit}
              />
            </Grid>
            <Grid item xs={12}>
              <CurrencyOption
                id={'destination'}
                options={destinations}
                label={'To'}
                value={destination}
                onChange={(newValue: string) =>
                  updateCurrencyCallback(newValue, () =>
                    setDestination(newValue)
                  )
                }
                disabled={isEdit}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                id="amount"
                name="amount"
                label="Amount"
                value={amount}
                onChange={(e) => handleAmountChange(e)}
                type="number"
                fullWidth
                required
                variant="standard"
                InputProps={{
                  inputProps: { min: 0, max: 100000 },
                }}
              />
            </Grid>
          </Grid>
          {errorMessage && <ErrorView message={errorMessage} />}
        </DialogBody>
      </FormDialogContainer>
    </>
  );
};

export default EditClientRate;
